import { useState } from "react";

interface PlatformCommission {
  platform: string;
  icon: string;
  color: string;
  bgColor: string;
  rate: number;
  enabled: boolean;
  resellers: number;
  salesThisMonth: number;
}

const defaultCommissions: PlatformCommission[] = [
  { platform: "TikTok", icon: "ri-tiktok-line", color: "text-gray-900", bgColor: "bg-gray-100", rate: 15, enabled: true, resellers: 342, salesThisMonth: 128 },
  { platform: "Instagram", icon: "ri-instagram-line", color: "text-pink-600", bgColor: "bg-pink-50", rate: 12, enabled: true, resellers: 218, salesThisMonth: 94 },
  { platform: "YouTube", icon: "ri-youtube-line", color: "text-red-600", bgColor: "bg-red-50", rate: 18, enabled: true, resellers: 97, salesThisMonth: 56 },
  { platform: "Twitter / X", icon: "ri-twitter-x-line", color: "text-gray-900", bgColor: "bg-gray-100", rate: 10, enabled: false, resellers: 44, salesThisMonth: 12 },
  { platform: "Douyin (抖音)", icon: "ri-tiktok-line", color: "text-gray-900", bgColor: "bg-gray-100", rate: 20, enabled: true, resellers: 189, salesThisMonth: 210 },
  { platform: "WeChat (微信)", icon: "ri-wechat-line", color: "text-green-600", bgColor: "bg-green-50", rate: 14, enabled: true, resellers: 76, salesThisMonth: 38 },
];

const ResellerCommissionPanel = () => {
  const [commissions, setCommissions] = useState<PlatformCommission[]>(defaultCommissions);
  const [saved, setSaved] = useState(false);
  const [globalRate, setGlobalRate] = useState("");

  const updateRate = (index: number, value: string) => {
    const num = Math.min(40, Math.max(1, parseInt(value) || 1));
    setCommissions((prev) =>
      prev.map((c, i) => (i === index ? { ...c, rate: num } : c))
    );
  };

  const toggleEnabled = (index: number) => {
    setCommissions((prev) =>
      prev.map((c, i) => (i === index ? { ...c, enabled: !c.enabled } : c))
    );
  };

  const applyGlobal = () => {
    const num = Math.min(40, Math.max(1, parseInt(globalRate) || 1));
    setCommissions((prev) => prev.map((c) => ({ ...c, rate: num })));
    setGlobalRate("");
  };

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const totalResellers = commissions.filter((c) => c.enabled).reduce((a, c) => a + c.resellers, 0);
  const totalSales = commissions.filter((c) => c.enabled).reduce((a, c) => a + c.salesThisMonth, 0);

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 flex items-center justify-center bg-[#D4AF37]/10 rounded-lg">
            <i className="ri-percent-line text-[#B8860B] text-lg"></i>
          </div>
          <div>
            <h2 className="font-bold text-gray-900 text-sm">Reseller Commission Settings</h2>
            <p className="text-[10px] text-gray-400">Set how much % you share with social media resellers per platform</p>
          </div>
        </div>
        <button
          onClick={handleSave}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-colors cursor-pointer whitespace-nowrap ${
            saved
              ? "bg-green-500 text-white"
              : "bg-[#C41E3A] text-white hover:bg-[#a01830]"
          }`}
        >
          <i className={saved ? "ri-check-line" : "ri-save-line"}></i>
          {saved ? "Saved!" : "Save Settings"}
        </button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 divide-x divide-gray-100 border-b border-gray-100">
        <div className="px-5 py-3 text-center">
          <div className="text-lg font-extrabold text-gray-900">{totalResellers.toLocaleString()}</div>
          <div className="text-[10px] text-gray-400">Active Resellers</div>
        </div>
        <div className="px-5 py-3 text-center">
          <div className="text-lg font-extrabold text-[#C41E3A]">{totalSales}</div>
          <div className="text-[10px] text-gray-400">Sales This Month</div>
        </div>
        <div className="px-5 py-3 text-center">
          <div className="text-lg font-extrabold text-[#D4AF37]">
            {commissions.filter((c) => c.enabled).length}/{commissions.length}
          </div>
          <div className="text-[10px] text-gray-400">Platforms Active</div>
        </div>
      </div>

      {/* Global Rate Setter */}
      <div className="px-5 py-3 bg-[#fdf8f8] border-b border-gray-100 flex items-center gap-3 flex-wrap">
        <span className="text-xs text-gray-500 font-semibold whitespace-nowrap">Apply same rate to all platforms:</span>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={1}
            max={40}
            value={globalRate}
            onChange={(e) => setGlobalRate(e.target.value)}
            placeholder="e.g. 15"
            className="w-20 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-[#C41E3A] text-center"
          />
          <span className="text-sm text-gray-500">%</span>
          <button
            onClick={applyGlobal}
            disabled={!globalRate}
            className="px-3 py-1.5 bg-[#D4AF37] text-[#1a0a0a] text-xs font-bold rounded-lg hover:bg-[#c9a227] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40"
          >
            Apply to All
          </button>
        </div>
        <span className="text-[10px] text-gray-400">Max 40% · Min 1%</span>
      </div>

      {/* Platform Rows */}
      <div className="divide-y divide-gray-50">
        {commissions.map((c, index) => (
          <div key={c.platform} className={`px-5 py-4 flex items-center gap-4 transition-colors ${!c.enabled ? "opacity-50" : ""}`}>
            {/* Platform Icon */}
            <div className={`w-10 h-10 flex items-center justify-center rounded-lg flex-shrink-0 ${c.bgColor}`}>
              <i className={`${c.icon} ${c.color} text-lg`}></i>
            </div>

            {/* Platform Name + Stats */}
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-gray-900 text-sm">{c.platform}</div>
              <div className="flex items-center gap-3 mt-0.5">
                <span className="text-[10px] text-gray-400">
                  <i className="ri-user-star-line mr-0.5"></i>{c.resellers} resellers
                </span>
                <span className="text-[10px] text-gray-400">
                  <i className="ri-shopping-bag-3-line mr-0.5"></i>{c.salesThisMonth} sales/mo
                </span>
              </div>
            </div>

            {/* Rate Slider + Input */}
            <div className="flex items-center gap-3 flex-shrink-0">
              <div className="hidden sm:flex items-center gap-2 w-32">
                <input
                  type="range"
                  min={1}
                  max={40}
                  value={c.rate}
                  onChange={(e) => updateRate(index, e.target.value)}
                  disabled={!c.enabled}
                  className="w-full accent-[#C41E3A] cursor-pointer"
                />
              </div>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  min={1}
                  max={40}
                  value={c.rate}
                  onChange={(e) => updateRate(index, e.target.value)}
                  disabled={!c.enabled}
                  className="w-14 border border-gray-200 rounded-lg px-2 py-1.5 text-sm font-bold text-center focus:outline-none focus:border-[#C41E3A] disabled:bg-gray-50"
                />
                <span className="text-sm font-bold text-gray-500">%</span>
              </div>
            </div>

            {/* Commission Preview */}
            <div className="hidden md:block text-right flex-shrink-0 w-24">
              <div className="text-xs text-gray-400">On $100 sale</div>
              <div className="text-sm font-bold text-[#C41E3A]">${c.rate} to reseller</div>
            </div>

            {/* Toggle */}
            <button
              onClick={() => toggleEnabled(index)}
              className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 ${
                c.enabled ? "bg-[#C41E3A]" : "bg-gray-200"
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                  c.enabled ? "translate-x-5" : "translate-x-0"
                }`}
              ></span>
            </button>
          </div>
        ))}
      </div>

      {/* Info Footer */}
      <div className="px-5 py-3 bg-[#fdf8f8] border-t border-gray-100">
        <div className="flex items-start gap-2 text-xs text-gray-500">
          <i className="ri-information-line text-[#D4AF37] mt-0.5 flex-shrink-0"></i>
          <span>
            Commission is automatically deducted from each sale and paid to resellers via <strong>PayPal</strong> on the 1st of every month.
            Resellers on TikTok, Instagram, YouTube, Twitter/X, Douyin, and WeChat can see your commission rate before choosing to promote your products.
          </span>
        </div>
      </div>
    </div>
  );
};

export default ResellerCommissionPanel;
